









































































































import { Inject, Watch, Prop, Component, Mixins } from 'vue-property-decorator'
import Financial from '@/views/Tasks/Detail/ReferralDetails/Financial.vue'
import { Order, OrderValueShare } from '@/api/model/order'
import { Referral } from '@/api/model/referral'
import { User } from '@/api/model/user'
import { v4 as uuid } from 'uuid'
import { Share, ShareType } from '@/api/model/share'
import { ShareAPI } from '@/api/backend-client'
import { Branch } from '@/api/model/branch'

@Component
export default class OrderFinancial extends Mixins(Financial) {
    @Inject() readonly saveOrder: (order: Order) => Promise<void>
    @Prop() readonly order!: Order
    @Prop() referral!: Referral
    selectedUser: User = null
    totalOrderValue: number = 0
    loading: boolean = false
    shares: Share[] = []
    team: Branch = null
    teamValue: number = 0
    initting: boolean = false
    manualPercentage: number = null

    async mounted() {
        await this.loadContent()

        // Ensure a team is present
        if (!this.teamShare) {
            this.order.orderValueShares.push(new OrderValueShare(
                uuid(),
                this.order.referral.referredByBranch,
                null,
                0,
                new Date(),
                new Date(),
            ))
        }

        // Copy team values
        this.initting = true
        this.team = this.teamShare.branch
        this.teamValue = this.teamShare.orderValue
        this.recalculateTotals()

        this.$nextTick(() => {
            this.initting = false

        })
        // Move users that are only in the involvedUsers array into shares
        this.order.involvedUsers.forEach(user => {
            if (this.userShares.some(share => share.user.id === user.id) === false) {
                this.selectedUser = user
            }
        })
    }

    async loadContent(): Promise<void> {
        this.loading = true
        this.shares = await ShareAPI.getAll()
        this.loading = false
    }

    /**
     * Ensure involvedUsers has the same users as in shares.
     * We already added new additions in the mounted() code above so it's safe to map.
     */
    async syncInvolvedUsers(): Promise<void> {
        this.order.involvedUsers = this.userShares.map(share => share.user)
    }

    async doSaveOrder(): Promise<void> {
        this.loading = true
        await this.saveOrder(this.order)
        this.loading = false
    }

    calculatePercentage(share: OrderValueShare): number {
        if (!share.orderValue) {
            return 0
        }

        return (share.orderValue / this.orderValueAfterTeamCommission) * 100
    }

    @Watch('selectedUser')
    addUserShare() {
        if (this.selectedUser === null) {
            return
        }

        if (this.userShares.some(share => share.user.id === this.selectedUser.id)) {
            this.selectedUser = null
            return
        }

        this.order.orderValueShares.push(OrderValueShare.fromData({
            id: uuid(),
            user: this.selectedUser,
        }))

        this.selectedUser = null
        this.syncInvolvedUsers()
        this.recalculateShares()
    }

    removeUserShare(share: OrderValueShare): void {
        this.order.orderValueShares = this.order.orderValueShares.filter(v => v.id !== share.id)
        this.syncInvolvedUsers()
        this.recalculateShares()
    }

    @Watch('order.orderValue')
    onOrderValueChanged(): void {
        this.teamValue = Number((((this.configuredPercentage / 100) * this.order.orderValue)).toFixed(2))
        this.recalculateShares()
    }

    @Watch('teamValue')
    recalculateShares(): void {
        if (!this.order.orderValue || this.order.orderValue === 0 || this.initting) {
            return
        }

        this.teamShare.orderValue = this.teamValue
        this.userShares.forEach(share => {
            share.orderValue = Number((this.orderValueAfterTeamCommission / this.userShares.length).toFixed(2))
        })

        const total = this.userShares
            .map(share => share.orderValue)
            .reduce((previousValue, currentValue) => Number(previousValue) + Number(currentValue), 0)

        const diff = this.orderValueAfterTeamCommission - total
        if (diff !== 0) {
            this.userShares[0].orderValue += diff
        }

        this.recalculateTotals()
    }

    onSharePercentageChanged(share: OrderValueShare, value: string): void {
        share.orderValue = (Number(value) / 100) * this.orderValueAfterTeamCommission
        this.recalculateTotals()
    }

    onShareValueChanged(share: OrderValueShare, value: string): void {
        share.orderValue = Number(value)
        this.recalculateTotals()
    }

    recalculateTotals(): void {
        if (this.userShares.length === 0) {
            this.totalOrderValue = 0
            return
        }
        this.totalOrderValue = this.userShares
            .map(share => share.orderValue)
            .reduce((previousValue, currentValue) => Number(previousValue) + Number(currentValue))
    }

    constrainDecimals(num: string|number): string {
        const value = Number(num)
        const parts = String(value).split('.')

        if (parts.length < 2) {
            return String(value)
        }

        const decimal = Number(parts[1])
        return decimal === 0 ? value.toFixed(0) : value.toFixed(2)
    }

    async adjustPercentage(): Promise<void> {
        const value = prompt('Welk percentage wil je instellen?')

        if (!Number.isNaN(value)) {
            this.manualPercentage = parseInt(value, 10)
            this.onOrderValueChanged()
        }
    }

    get isExternal(): boolean {
        return this.order.referral.referredByBranch.company.id !== this.order.referral.assignedToBranch.company.id
    }

    get configuredShare(): Share|null {
        if (!this.order) {
            return null
        }

        return this.shares
            .filter(share => share.type === (this.isExternal ? ShareType.EXTERNAL : ShareType.INTERNAL))
            .find(share => share.orderType === this.order.type)
    }

    // Do not use this to display, this is only for initial/fallback
    get configuredPercentage(): number {
        if (this.manualPercentage > 0) {
            return this.manualPercentage
        }

        return this.configuredShare ? Number(this.configuredShare.percentage) : 20
    }

    get availableUsers(): User[] {
        return this.$currentCompany.getAvailableUsers()
            .filter(user => !this.userShares.some(share => share.user.id === user.id))
            .sort((a: User, b: User) => a.name.localeCompare(b.name))
            .sort(((a: User) => a.id === this.$currentUser.id ? -1 : 1))
    }

    /**
     * This is the amount of money left to be divided after the team commission has been taken.
     */
    get orderValueAfterTeamCommission(): number {
        return this.order.orderValue - Number(this.teamValue)
    }

    /**
     * Calculate total order value percentage based on each individual orderValue
     * This value can deviate from 100%
     */
    get totalOrderPercentage(): number {
        if (this.totalOrderValue === 0 || Number.isNaN(this.totalOrderValue)) {
            return 100
        }

        return Number(Number((this.totalOrderValue / this.orderValueAfterTeamCommission) * 100).toFixed(2))
    }

    get teamCommissionPercentage(): number {
        if (!this.teamValue || !this.order.orderValue) {
            return !this.configuredShare ? 20 : Number(this.configuredShare.percentage)
        }

        return (this.teamValue / this.order.orderValue) * 100
    }

    get userShares(): OrderValueShare[] {
        return this.order.orderValueShares
            .filter(share => share.user !== null)
            .sort((a, b) => a.user.name.toLowerCase().localeCompare(b.user.name.toLowerCase()))
    }

    get teamShare(): OrderValueShare|undefined {
        return this.order.orderValueShares
            .find(share => share.branch !== null)
    }
}
